/**
 * Onboarding wizard step config — single source of truth.
 *
 * Every consumer (header counter, progress bar, router, store) reads from
 * STEPS so adding/removing a step never desynchronizes the UI.
 */

import type { Icon as LucideIcon } from "@phosphor-icons/react";
import { Sparkle as Sparkles, ChartLineUp as Stethoscope, Target, ChatCircle as MessageSquare, Users, Wrench, Compass, Fingerprint, Key } from "@phosphor-icons/react";

export interface StepConfig {
  /** Stable numeric id (used for analytics/telemetry, NOT array index). */
  readonly id: number;
  /** URL slug for `?step=` deep link. */
  readonly slug: string;
  /** Header counter title (`Passo N de TOTAL · {title}`). */
  readonly title: string;
  /** Hero icon component. */
  readonly icon: LucideIcon;
  /** H1 inside StepContainer. */
  readonly headline: string;
  /** Subhead under H1. */
  readonly sub: string;
  /** True for steps not yet implemented (renders PlaceholderStep). */
  readonly placeholder?: boolean;
  /** True for steps that can be skipped — only the required steps gate
   *  onboarding completion and the OnboardingNudge dismissal threshold. */
  readonly optional?: boolean;
}

export const STEPS: readonly StepConfig[] = [
  {
    id: 9,
    slug: "conexoes",
    title: "Conexões IA",
    icon: Key,
    headline: "Conecte sua IA.",
    sub: "Sua chave, seu custo. OpenAI é usada para transcrição, análise e geração com GPT-5.6.",
  },
  {
    id: 1,
    slug: "identidade",
    title: "Identidade",
    icon: Sparkles,
    headline: "Vamos começar pelo básico.",
    sub: "Em 2 minutos por etapa, sua IA aprende quem você é.",
  },
  {
    id: 2,
    slug: "profissional",
    title: "Profissional",
    icon: Stethoscope,
    headline: "Sua atuação.",
    sub: "Conta pra gente onde você atua e o que te credencia.",
  },
  {
    id: 3,
    slug: "posicionamento",
    title: "Posicionamento",
    icon: Target,
    headline: "Seu posicionamento.",
    sub: "O que faz você diferente do mercado.",
    optional: true,
  },
  {
    id: 8,
    slug: "codigo-interno",
    title: "Código Interno",
    icon: Fingerprint,
    headline: "Sua personalidade.",
    sub: "O Eneagrama identifica seu padrão de comunicação. A IA usa pra calibrar tudo ao seu estilo.",
    optional: true,
  },
  {
    id: 4,
    slug: "voz",
    title: "Voz",
    icon: MessageSquare,
    headline: "Seu jeito de falar.",
    sub: "Tom, palavras-âncora e o que você nunca diria.",
    optional: true,
  },
  {
    id: 5,
    slug: "procedimentos",
    title: "Procedimentos",
    icon: Wrench,
    headline: "O que você oferece.",
    sub: "Procedimentos, valores e formato.",
    optional: true,
  },
  {
    id: 7,
    slug: "funil",
    title: "Funil",
    icon: Compass,
    headline: "Como o cliente chega.",
    sub: "Seus canais, ofertas e jornada.",
    optional: true,
  },
  {
    id: 6,
    slug: "publico",
    title: "Público",
    icon: Users,
    headline: "Para quem você fala.",
    sub: "Resumo do seu cliente ideal — gerado a partir de tudo que você já preencheu.",
    optional: true,
  },
] as const;

export const TOTAL_STEPS = STEPS.length;

/** Number of required (non-optional) steps. Used by OnboardingNudge and
 *  guard logic so the user is no longer nagged once required steps are
 *  filled, even if optional steps remain empty. */
export const REQUIRED_STEPS_COUNT = STEPS.filter((s) => !s.optional).length;

/** Completeness threshold (%) above which onboarding is treated as
 *  "good enough": user finished the required steps.
 *
 *  Reconciliation with backend (S1-DEV-5, 2026-05-07):
 *  - Server-side `completeness_pct` is computed by
 *    `public.compute_completeness_row()` (see migration
 *    20260427121000_fix_completeness_trigger.sql). Weights are NOT even.
 *    Totals: Identity=10, Professional=15, Positioning=20, Procedures=15,
 *    Voice=10, Persona=15, Notes=5, Offers+Constraints=10 → sum 100.
 *  - The 3 required STEPS (conexoes/BYOK, identidade, profissional) map to
 *    a max contribution of 0 + 10 + 15 = 25% in the backend score (BYOK
 *    keys live in `user_api_keys` and intentionally don't affect this pct).
 *    Hence 25 = "all required steps fully filled". A user who skips an
 *    optional sub-field (e.g. photo_url, -4pts) inside a required step
 *    will land below 25 and still see the nudge — acceptable given pct
 *    drives prompt quality, not just gating.
 *  - Deeper features may gate at higher pcts via
 *    useOnboardingGuard.requireMinPct(). */
export const REQUIRED_COMPLETENESS_PCT = 25;

export function findStepIndexBySlug(slug: string | null | undefined): number {
  if (!slug) return 0;
  const i = STEPS.findIndex((s) => s.slug === slug);
  return i >= 0 ? i : 0;
}
